import type { PlaygroundNodeData } from "@/utils/playground/workflowToNodes";

import React from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Icon } from "@iconify/react";
import { Chip, Tooltip } from "@heroui/react";
import { useTranslation } from "react-i18next";

import { useTheme } from "@/hooks/use-theme";
import { usePlaygroundStore } from "@/stores/PlaygroundStore";

import { StatusBadge } from "./StatusBadge";

const PlaygroundTeamNode: React.FC<NodeProps> = ({ data, selected }) => {
  const nodeData = data as PlaygroundNodeData;
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { setSelectedNode, setPropertiesPanelOpen } = usePlaygroundStore();

  const strategy = nodeData.teamStrategy || "selector";
  const members = nodeData.teamMembers || [];
  const goal = nodeData.teamGoal || "";
  const validationErrors = nodeData.validationErrors || [];
  const hasErrors = validationErrors.length > 0;

  const strategyLabel = strategy === "selector" ? t("Selector") : t("Mixture of Agents");
  const strategyColour = strategy === "selector" ? "secondary" : "warning";
  const status = nodeData.status;

  const getBorderClass = () => {
    if (hasErrors) return isDark ? "border-red-500" : "border-red-400";
    if (status === "running") return "border-warning animate-pulse";
    if (status === "success") return "border-success";
    if (status === "error") return "border-danger";

    return isDark ? "border-purple-500/50" : "border-purple-300";
  };

  return (
    <div
      className={`
        relative px-4 py-3 rounded-xl border-2 border-dashed min-w-[240px] max-w-[300px] transition-all duration-200
        ${selected ? "ring-2 ring-purple-400 ring-offset-2" : ""}
        ${isDark ? "bg-default-100" : "bg-white"}
        ${getBorderClass()}
        shadow-md hover:shadow-lg
      `}
    >
      <div className="flex items-center gap-3">
        <div
          className={`
            rounded-full w-10 h-10 flex items-center justify-center shrink-0
            ${isDark ? "bg-purple-500/20" : "bg-purple-50"}
          `}
        >
          <Icon
            className={`w-5 h-5 ${isDark ? "text-purple-400" : "text-purple-600"}`}
            icon="solar:users-group-rounded-linear"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div
            className={`text-sm font-semibold truncate ${isDark ? "text-default-800" : "text-purple-900"}`}
          >
            {nodeData.label}
          </div>
          <Chip className="text-tiny mt-0.5" color={strategyColour} size="sm" variant="flat">
            {strategyLabel}
          </Chip>
        </div>
      </div>

      {/* Members list */}
      <div className="mt-2 space-y-1">
        <div className="flex items-center gap-1">
          <Icon
            className={`w-3.5 h-3.5 ${isDark ? "text-default-500" : "text-gray-400"}`}
            icon="solar:users-group-two-rounded-linear"
          />
          <span className={`text-xs ${isDark ? "text-default-500" : "text-gray-500"}`}>
            {members.length} {members.length === 1 ? t("member") : t("members")}
          </span>
        </div>
        {members.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {members.slice(0, 3).map((memberId) => (
              <Chip
                key={memberId}
                className="text-tiny cursor-pointer hover:opacity-80"
                color="primary"
                size="sm"
                variant="flat"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedNode(memberId);
                  setPropertiesPanelOpen(true);
                }}
              >
                {memberId}
              </Chip>
            ))}
            {members.length > 3 && (
              <Chip className="text-tiny" color="default" size="sm" variant="flat">
                +{members.length - 3} more
              </Chip>
            )}
          </div>
        )}
      </div>

      {/* Goal preview */}
      {goal && (
        <div
          className={`mt-2 text-xs truncate ${isDark ? "text-default-400" : "text-gray-400"}`}
          title={goal}
        >
          {goal}
        </div>
      )}

      {hasErrors && (
        <Tooltip
          content={
            <div className="max-w-xs">
              {validationErrors.map((err, i) => (
                <p key={i} className="text-xs">
                  {err}
                </p>
              ))}
            </div>
          }
        >
          <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
            <Icon className="w-3 h-3 text-white" icon="solar:danger-triangle-linear" />
          </div>
        </Tooltip>
      )}

      {nodeData.status && !hasErrors && (
        <div className="absolute -top-2 -right-2">
          <StatusBadge isCurrentStep={nodeData.isCurrentStep || false} status={nodeData.status} />
        </div>
      )}

      <Handle
        className="!w-3 !h-3 !bg-purple-500 !border-2 !border-white"
        position={Position.Left}
        type="target"
      />
      <Handle
        className="!w-3 !h-3 !bg-purple-500 !border-2 !border-white"
        position={Position.Right}
        type="source"
      />
    </div>
  );
};

export default PlaygroundTeamNode;
